import { useState, useMemo, useCallback } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronDown, ChevronRight, Search, Repeat, Car, UserPlus, X, Info } from 'lucide-react';
import { AdfElementNode } from '@/hooks/useAdfStandard';

interface AdfBuilderSidebarProps {
  elementTree: AdfElementNode[];
  selectedElements: Set<string>;
  onToggle: (id: string) => void;
  onSelectRequired: () => void;
  onClearSelection: () => void;
  onSelectAll: () => void;
  activeTemplate?: string;
  // Phase 5: Quick Actions
  onAddTradeIn?: () => void;
  onRemoveTradeIn?: () => void;
  onAddCoBuyer?: () => void;
  onRemoveCoBuyer?: () => void;
  tradeInEnabled?: boolean;
  coBuyerEnabled?: boolean;
  hasVehicleSelected?: boolean;
  hasCustomerSelected?: boolean;
}

interface TreeNodeProps {
  node: AdfElementNode;
  level: number;
  isSelected: boolean;
  expandedNodes: Set<string>;
  selectedElements: Set<string>;
  onToggle: (id: string) => void;
  onToggleExpand: (id: string) => void;
  searchQuery: string;
}

function TreeNode({
  node,
  level,
  isSelected,
  expandedNodes,
  selectedElements,
  onToggle,
  onToggleExpand,
  searchQuery,
}: TreeNodeProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  
  // Check if any descendants match search
  const matchesSearch = (element: AdfElementNode): boolean => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (element.tag_name.toLowerCase().includes(query)) return true;
    if (element.display_name.toLowerCase().includes(query)) return true;
    return element.children.some(matchesSearch);
  };
  
  // Check if this node or any child matches
  const nodeMatches = useMemo(() => matchesSearch(node), [node, searchQuery]);
  
  if (!nodeMatches) return null;
  
  // Check if any children are selected
  const hasSelectedChildren = useMemo(() => {
    const checkChildren = (el: AdfElementNode): boolean => {
      if (selectedElements.has(el.id)) return true;
      return el.children.some(checkChildren);
    };
    return node.children.some(checkChildren);
  }, [node.children, selectedElements]);

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors hover:bg-muted/50 ${
          isSelected ? 'bg-primary/10' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => onToggleExpand(node.id)}
            className="p-0.5 hover:bg-muted rounded shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-[18px]" />
        )}
        
        <Checkbox
          id={`builder-${node.id}`}
          checked={isSelected}
          onCheckedChange={() => onToggle(node.id)}
          className="shrink-0"
        />
        
        <label
          htmlFor={`builder-${node.id}`}
          className="flex-1 flex items-center gap-2 cursor-pointer min-w-0"
        >
          <code className="text-xs font-mono text-primary shrink-0">
            &lt;{node.tag_name}&gt;
          </code>
          <span className="text-sm text-muted-foreground truncate">
            {node.display_name}
          </span>
        </label>
        
        <div className="flex items-center gap-1 shrink-0">
          {node.is_required && (
            <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4">
              req
            </Badge>
          )}
          {node.is_repeatable && (
            <Repeat className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
        
        {hasSelectedChildren && !isSelected && (
          <span className="w-2 h-2 rounded-full bg-primary/50 shrink-0" />
        )}
      </div>
      
      {hasChildren && isExpanded && (
        <div>
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              isSelected={selectedElements.has(child.id)}
              expandedNodes={expandedNodes}
              selectedElements={selectedElements}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdfBuilderSidebar({
  elementTree,
  selectedElements,
  onToggle,
  onSelectRequired,
  onClearSelection,
  onSelectAll,
  activeTemplate,
  onAddTradeIn,
  onRemoveTradeIn,
  onAddCoBuyer,
  onRemoveCoBuyer,
  tradeInEnabled = false,
  coBuyerEnabled = false,
  hasVehicleSelected = false,
  hasCustomerSelected = false,
}: AdfBuilderSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    // Start with first level expanded
    const initial = new Set<string>();
    elementTree.forEach(el => initial.add(el.id));
    return initial;
  });
  
  // Count total elements
  const totalCount = useMemo(() => {
    const countAll = (nodes: AdfElementNode[]): number => {
      return nodes.reduce((acc, node) => acc + 1 + countAll(node.children), 0);
    };
    return countAll(elementTree);
  }, [elementTree]);
  
  const toggleExpand = useCallback((id: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);
  
  const expandAll = useCallback(() => {
    const getAllIds = (nodes: AdfElementNode[]): string[] => {
      return nodes.flatMap(node => [node.id, ...getAllIds(node.children)]);
    };
    setExpandedNodes(new Set(getAllIds(elementTree)));
  }, [elementTree]);
  
  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-2 p-4 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search elements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onSelectRequired}>
            Select Required
          </Button>
          <Button variant="outline" size="sm" onClick={onSelectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={onClearSelection}>
            Clear
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs">
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs">
            Collapse All
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      {(onAddTradeIn || onAddCoBuyer) && (
        <div className="px-4 py-3 border-b bg-muted/30">
          <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
            Quick Actions
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p>Add common multi-element scenarios like trade-in vehicles or co-buyers.</p>
              </TooltipContent>
            </Tooltip>
          </h4>
          <div className="flex flex-col gap-1.5">
            {onAddTradeIn && (
              <Button
                variant={tradeInEnabled ? "secondary" : "outline"}
                size="sm"
                onClick={tradeInEnabled ? onRemoveTradeIn : onAddTradeIn}
                disabled={!hasVehicleSelected && !tradeInEnabled}
                className="justify-start gap-2 h-8"
              >
                <Car className="h-3.5 w-3.5" />
                {tradeInEnabled ? 'Remove Trade-In' : 'Add Trade-In Vehicle'}
                {tradeInEnabled && <Badge variant="default" className="ml-auto text-[10px] px-1.5 py-0">Active</Badge>}
              </Button>
            )}
            {onAddCoBuyer && (
              <Button
                variant={coBuyerEnabled ? "secondary" : "outline"}
                size="sm"
                onClick={coBuyerEnabled ? onRemoveCoBuyer : onAddCoBuyer}
                disabled={!hasCustomerSelected && !coBuyerEnabled}
                className="justify-start gap-2 h-8"
              >
                <UserPlus className="h-3.5 w-3.5" />
                {coBuyerEnabled ? 'Remove Co-Buyer' : 'Add Co-Buyer'}
                {coBuyerEnabled && <Badge variant="default" className="ml-auto text-[10px] px-1.5 py-0">Active</Badge>}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="px-4 py-2 text-sm text-muted-foreground border-b flex items-center justify-between">
        <span>{selectedElements.size} of {totalCount} elements selected</span>
        {activeTemplate && (
          <Badge variant="secondary" className="text-xs">
            {activeTemplate}
          </Badge>
        )}
      </div>

      <ScrollArea className="flex-1">
        <Collapsible defaultOpen>
          <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-medium text-sm hover:text-primary transition-colors p-4 pb-2">
            <ChevronDown className="h-4 w-4" />
            ADF Elements
          </CollapsibleTrigger>
          <CollapsibleContent className="px-2 pb-4">
            {elementTree.map(node => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                isSelected={selectedElements.has(node.id)}
                expandedNodes={expandedNodes}
                selectedElements={selectedElements}
                onToggle={onToggle}
                onToggleExpand={toggleExpand}
                searchQuery={searchQuery}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      </ScrollArea>
    </div>
  );
}
